<div class="kt-container-fixed">
	<div class="flex flex-col gap-6 pb-6">
		<div class="flex flex-wrap items-end justify-between gap-3">
			<div class="flex flex-col gap-2">
				<div class="flex flex-wrap items-center gap-2">
					<h1 class="font-semibold text-2xl text-mono">{{ calculationType }} Historical</h1>
					<span class="kt-badge kt-badge-sm kt-badge-outline whitespace-nowrap">{{ referenceDateLabel }}</span>
				</div>
				<p class="text-sm text-secondary-foreground">{{ calculationType }} calculations with approval, publication, and rejection traceability.</p>
			</div>
			@if (loadError) {
				<div class="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-danger/30 bg-danger/10 p-3" role="alert">
					<div class="flex items-center gap-2.5"><i class="ki-filled ki-information-2 text-danger"></i><span class="text-sm">{{ loadError }}</span></div>
					<button class="kt-btn kt-btn-sm kt-btn-outline" type="button" (click)="retry.emit()"><i class="ki-filled ki-arrows-circle"></i>Retry</button>
				</div>
			}
			<button *appPermission="calculationType.toLowerCase() + '-calculation'" class="kt-btn kt-btn-primary" type="button" (click)="calculationOpened.emit(undefined)">
				<i class="ki-filled ki-calculator"></i>
				New calculation
			</button>
		</div>

		<section class="kt-card kt-card-grid min-w-full">
			<div class="kt-card-header flex-wrap gap-3 py-4">
				<div class="flex flex-col gap-1">
					<h2 class="kt-card-title">Calculation history</h2>
					<span class="text-xs text-secondary-foreground">Select a record to open {{ calculationType }} Calculation</span>
				</div>
				<div class="flex flex-row flex-nowrap items-center gap-2.5 overflow-x-auto">
					<label class="kt-input w-52 shrink-0"><i class="ki-filled ki-magnifier"></i><input type="text" placeholder="Search history" [attr.aria-label]="'Search ' + calculationType + ' history'" (input)="searchChanged.emit($event)" /></label>
					@if (calculationType === 'LCF') {
					<select class="kt-select w-36 shrink-0" aria-label="Filter currency" (change)="currencyChanged.emit($event)">
						<option value="All">All currencies</option>
						@for (currency of currencies; track currency) {
							<option [value]="currency">{{ currency }}</option>
						}
					</select>
					}
					<select class="kt-select w-44 shrink-0" aria-label="Filter workflow status" (change)="statusChanged.emit($event)">
						<option value="All">All statuses</option>
						<option value="PENDING_APPROVAL">Pending Approval</option>
						<option value="APPROVED">Approved</option>
						<option value="PUBLISHED">Published</option>
						<option value="REJECTED">Rejected</option>
						<option value="PUBLICATION_REJECTED">Publication Rejected</option>
					</select>
					<span class="kt-badge kt-badge-sm kt-badge-outline whitespace-nowrap">{{ filteredRecords.length }} records</span>
				</div>
			</div>

			<div class="kt-card-content relative p-0" [class.min-h-48]="isLoading" [attr.aria-busy]="isLoading">
				@if (isLoading) {
					<div class="absolute inset-0 z-30 flex items-center justify-center rounded-b-xl bg-background/90" role="status" aria-live="polite">
						<span class="inline-flex items-center gap-2 text-sm text-secondary-foreground">
							<i class="ki-filled ki-arrows-circle animate-spin motion-reduce:animate-none text-primary" aria-hidden="true"></i>
							Loading {{ calculationType }} history...
						</span>
					</div>
				}
				<div class="kt-scrollable-x-auto max-h-[55vh] overflow-y-auto" [inert]="isLoading">
					<table class="kt-table table-auto kt-table-border min-w-[2560px]">
						<thead class="sticky top-0 z-10 bg-background shadow-sm">
							<tr>
								<th class="min-w-[110px] text-center">View</th>
								<th class="min-w-[130px]">Date</th>
								<th class="min-w-[150px]">Workflow Status</th>
								@if (calculationType === 'LCF') { <th class="min-w-[140px]">Book</th> }
								@if (calculationType === 'LCF') { <th class="min-w-[100px]">Currency</th> }
								<th class="min-w-[130px] text-right bg-primary/5">Trade Spread</th>
								<th class="min-w-[190px] text-right">Total Current Balance</th>
								<th class="min-w-[190px] text-right">Total Balance Spread</th>
								<th class="min-w-[140px]">Saved By</th>
								<th class="min-w-[190px]">Saved On</th>
								<th class="min-w-[140px]">Approved By</th>
								<th class="min-w-[190px]">Approved On</th>
								<th class="min-w-[260px]">Approved Comment</th>
								<th class="min-w-[140px]">Published By</th>
								<th class="min-w-[190px]">Published On</th>
								<th class="min-w-[260px]">Published Comment</th>
								<th class="min-w-[140px]">Rejected By</th>
								<th class="min-w-[190px]">Rejected On</th>
								<th class="min-w-[250px]">Rejection Reason</th>
								<th class="min-w-[140px]">Publication Rejected By</th>
								<th class="min-w-[190px]">Publication Rejected On</th>
								<th class="min-w-[250px]">Publication Rejection Reason</th>
							</tr>
						</thead>
						<tbody>
							@for (record of filteredRecords; track record.cobLcfFffHistId) {
								<tr class="hover:bg-muted/40">
									<td class="text-center"><button class="kt-btn kt-btn-sm kt-btn-outline" type="button" [attr.aria-label]="'View ' + calculationType + ' calculation'" (click)="$event.stopPropagation(); calculationOpened.emit(record.cobLcfFffHistId)"><i class="ki-filled ki-eye"></i>View</button></td>
									<td class="font-medium text-mono whitespace-nowrap">{{ formatDate(record.date) }}</td>
									<td>
										@switch (record.workflowStatus) {
											@case ('PUBLISHED') { <span class="kt-badge kt-badge-sm kt-badge-info kt-badge-outline whitespace-nowrap"><span class="kt-badge-dot"></span>Published</span> }
											@case ('APPROVED') { <span class="kt-badge kt-badge-sm kt-badge-success kt-badge-outline whitespace-nowrap"><span class="kt-badge-dot"></span>Approved</span> }
											@case ('REJECTED') { <span class="kt-badge kt-badge-sm kt-badge-destructive kt-badge-outline whitespace-nowrap"><span class="kt-badge-dot"></span>Rejected</span> }
											@case ('PUBLICATION_REJECTED') { <span class="kt-badge kt-badge-sm kt-badge-destructive kt-badge-outline whitespace-nowrap"><span class="kt-badge-dot"></span>Publication Rejected</span> }
											@default { <span class="kt-badge kt-badge-sm kt-badge-warning kt-badge-outline whitespace-nowrap"><span class="kt-badge-dot"></span>Pending Approval</span> }
										}
									</td>
									@if (calculationType === 'LCF') { <td>{{ record.book || '—' }}</td> }
									@if (calculationType === 'LCF') { <td><span class="kt-badge kt-badge-sm kt-badge-outline">{{ record.currency || '—' }}</span></td> }
									<td class="text-right tabular-nums font-semibold bg-primary/5">{{ formatNumber(record.tradeSpread) }}</td>
									<td class="text-right tabular-nums">{{ formatNumber(record.totalCurrentBalance) }}</td>
									<td class="text-right tabular-nums">{{ formatNumber(record.totalBalanceSpread) }}</td>
									<td>{{ record.savedBy || '—' }}</td>
									<td class="text-secondary-foreground whitespace-nowrap">{{ formatDate(record.savedOn, true) }}</td>
									<td>{{ record.approvedBy || '—' }}</td>
									<td class="text-secondary-foreground whitespace-nowrap">{{ formatDate(record.approvedOn, true) }}</td>
									<td class="max-w-[320px] whitespace-normal">{{ record.approvedComment || '—' }}</td>
									<td>{{ record.publishedBy || '—' }}</td>
									<td class="text-secondary-foreground whitespace-nowrap">{{ formatDate(record.publishedOn, true) }}</td>
									<td class="max-w-[320px] whitespace-normal">{{ record.publishedComment || '—' }}</td>
									<td>{{ record.rejectedBy || '—' }}</td>
									<td class="whitespace-nowrap">{{ formatDate(record.rejectedOn, true) }}</td>
									<td class="whitespace-pre-wrap break-words">{{ record.rejectedReason || '—' }}</td>
									<td>{{ record.publicationRejectedBy || '—' }}</td>
									<td class="whitespace-nowrap">{{ formatDate(record.publicationRejectedOn, true) }}</td>
									<td class="whitespace-pre-wrap break-words">{{ record.publicationRejectedReason || '—' }}</td>
								</tr>
							} @empty {
								@if (!isLoading) {
									<tr><td [attr.colspan]="calculationType === 'FFF' ? 20 : 22" class="py-10 text-center text-secondary-foreground">No historical records match the current filters.</td></tr>
								}
							}
						</tbody>
					</table>
				</div>
			</div>
		</section>
	</div>
</div>
